// frontend/src/App.js
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import FirstLoginPassword from './pages/auth/FirstLoginPassword';
import AgentDashboard from './pages/dashboard/AgentDashboard';
import BrokerDashboard from './pages/dashboard/BrokerDashboard';
import ClientDashboard from './pages/dashboard/ClientDashboard';
import UnderwriterDashboard from './pages/dashboard/UnderwriterDashboard';
import AdminDashboard from './pages/dashboard/AdminDashboard';
import QuoteList from './pages/quotes/QuoteList';
import QuoteCreate from './pages/quotes/QuoteCreate';
import QuoteView from './pages/quotes/QuoteView';
import OpenCoverList from './pages/quotes/OpenCoverList';
import OpenCoverCreate from './pages/quotes/OpenCoverCreate';
import OpenCoverDetail from './pages/quotes/OpenCoverDetail';
import VesselTracker from './pages/vessels/VesselTracker';
import ClearingAgents from './pages/clearing/ClearingAgents';
import IDFInsuranceCheck from './pages/cargo/IDFInsuranceCheck';
import PolicyList from './pages/policies/PolicyList';
import PolicyView from './pages/policies/PolicyView';
import CargoFlow from './pages/cargo/CargoFlow';
import ReportPage from './pages/reports/ReportPage';
import ProfilePage from './pages/auth/ProfilePage';
import SettingsPage from './pages/auth/SettingsPage';
import PortManagement from './pages/ports/PortManagement';
import PrivateRoute from './components/layout/PrivateRoute';
import Layout from './components/layout/Layout';
import CertificatesList from './pages/cargo/CertificatesList';
import CertificateDetail from './pages/cargo/CertificateDetail';
import useSessionTimeout from './hooks/useSessionTimeout';
import SessionWarning from './components/ui/SessionWarning';

function App() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/first-login-password" element={<FirstLoginPassword />} />

            {/* Protected Routes (require login) */}
            <Route element={<PrivateRoute />}>
                <Route element={<LayoutWithSession />}>
                    {/* Dashboard (role-based) */}
                    <Route path="/dashboard" element={<Dashboard />} />

                    {/* Quotes */}
                    <Route path="/quotes" element={<QuoteList />} />
                    <Route path="/quotes/new" element={<QuoteCreate />} />
                    <Route path="/quotes/:id" element={<QuoteView />} />

                    {/* Open Covers */}
                    <Route path="/open-covers" element={<OpenCoverList />} />
                    <Route path="/open-covers/new" element={<OpenCoverCreate />} />
                    <Route path="/open-covers/:id" element={<OpenCoverDetail />} />

                    {/* Vessel Tracking */}
                    <Route path="/vessel-tracker" element={<VesselTracker />} />

                    {/* Clearing Agents */}
                    <Route path="/clearing-agents" element={<ClearingAgents />} />

                    {/* Insured Cargo */}
                    <Route path="/idf-check" element={<IDFInsuranceCheck />} />

                    {/* Policies */}
                    <Route path="/policies" element={<PolicyList />} />
                    <Route path="/policies/:id" element={<PolicyView />} />

                    {/* Cargo Certificate Flow */}
                    <Route path="/cargo" element={<CargoFlow />} />

                    {/* Certificate Vault (list + detail) */}
                    <Route path="/cargo/certificates" element={<CertificatesList />} />
                    <Route path="/cargo/certificates/:id" element={<CertificateDetail />} />

                    {/* Reports */}
                    <Route path="/reports" element={<ReportPage />} />

                    {/* Underwriter Routes */}
                    <Route path="/underwriter/rates" element={<UnderwriterDashboard />} />
                    <Route path="/underwriter/rates/new" element={<UnderwriterDashboard />} />

                    {/* Admin Routes */}
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/ports" element={<PortManagement />} />

                    {/* User Profile & Settings */}
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                </Route>
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
    );
}

// Wrapper to include session timeout inside protected routes
function LayoutWithSession() {
    const { showWarning, timeLeft, handleStayLoggedIn, handleLogout } = useSessionTimeout();
    return (
        <>
            <SessionWarning
                show={showWarning}
                timeLeft={timeLeft}
                onStayLoggedIn={handleStayLoggedIn}
                onLogout={handleLogout}
            />
            <Layout />
        </>
    );
}

// Role-based dashboard selector
function Dashboard() {
    const { user } = useSelector((state) => state.auth);
    switch (user?.role) {
        case 'agent': return <AgentDashboard />;
        case 'broker': return <BrokerDashboard />;
        case 'direct_client': return <ClientDashboard />;
        case 'underwriter': return <UnderwriterDashboard />;
        case 'admin': return <AdminDashboard />;
        default: return <Navigate to="/login" />;
    }
}

export default App;